
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTasks } from '@/context/TaskContext';
import { 
  createTimeEntry, 
  updateTimeEntry, 
  finalizeTimeEntry, 
  getActiveTimeEntry,
  cleanupInProgressEntries 
} from '@/context/TaskContext/timeEntriesApi';

export const useActiveTimeEntry = () => {
  const { user } = useAuth();
  const { activeTask } = useTasks();
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const startTimeEntry = async () => {
    if (!user || !activeTask) return null;

    try {
      await cleanupInProgressEntries(user.id, activeTask.id);

      const entryId = await createTimeEntry(
        activeTask.id,
        user.id,
        0,
        'pomodoro',
        'in_progress'
      );

      if (entryId) {
        setActiveEntryId(entryId);
        setStartTime(new Date());
        startPeriodicUpdates();
        return entryId;
      }
    } catch (error) {
      console.error('Failed to start time entry:', error);
    }
    return null;
  };

  const updateCurrentEntry = async (forceUpdate = false) => {
    if (!activeEntryId || !startTime) return;

    const elapsedSeconds = Math.floor((Date.now() - startTime.getTime()) / 1000);
    
    if (elapsedSeconds > 10 || forceUpdate) {
      try {
        await updateTimeEntry(activeEntryId, elapsedSeconds);
      } catch (error) {
        console.error('Failed to update time entry:', error);
      }
    }
  };

  const finalizeCurrentEntry = async (sessionStatus: 'completed' | 'interrupted' = 'completed') => {
    if (!activeEntryId || !startTime) return;

    const finalDuration = Math.floor((Date.now() - startTime.getTime()) / 1000);
    
    try {
      await finalizeTimeEntry(activeEntryId, finalDuration, sessionStatus);
      
      stopPeriodicUpdates();
      setActiveEntryId(null);
      setStartTime(null);
      
      return finalDuration;
    } catch (error) {
      console.error('Failed to finalize time entry:', error);
      return 0;
    }
  };

  const startPeriodicUpdates = () => {
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
    }
    
    updateIntervalRef.current = setInterval(() => {
      updateCurrentEntry();
    }, 30000);
  };

  const stopPeriodicUpdates = () => {
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopPeriodicUpdates();
    };
  }, []);

  useEffect(() => {
    if (activeEntryId && activeTask) {
      const checkTaskChange = async () => {
        try {
          const activeEntry = await getActiveTimeEntry(user?.id || '', activeTask.id);
          if (!activeEntry || activeEntry.id !== activeEntryId) {
            await finalizeCurrentEntry('interrupted');
          }
        } catch (error) {
          console.error('Error checking task change:', error);
        }
      };
      
      checkTaskChange();
    }
  }, [activeTask?.id]);

  return {
    activeEntryId,
    isTracking: !!activeEntryId,
    startTimeEntry,
    updateCurrentEntry,
    finalizeCurrentEntry,
    getCurrentDuration: () => startTime ? Math.floor((Date.now() - startTime.getTime()) / 1000) : 0
  };
};
